"use client";

import { type RefObject, useLayoutEffect } from "react";

/**
 * CSS custom property on `<html>` holding the rendered height of the page's
 * fixed bottom conversion bar (job-detail apply bar, pricing sticky CTA), or
 * `0px` when none is mounted/visible. The cookie banner reads it to sit ABOVE
 * that bar.
 *
 * Direction matters for CLS. The old contract ran the other way — the banner
 * published `--consent-offset` and the bars moved their own `bottom` to
 * follow it. The bar is server-rendered and already painted, so every change
 * of that offset (banner mount, CMP resolution, show/hide) moved an existing
 * fixed element: 3–5 shifts of 0.0435 per load, mobile job-detail CLS
 * 0.10–0.14 (2026-09-27). Now the bar never moves; the banner — inserted after
 * hydration, and an insertion is not a layout shift — takes the offset.
 */
export const STICKY_CTA_HEIGHT_VAR = "--sticky-cta-h";

/**
 * `cssVar` defaults to `--sticky-cta-h`. The same publisher also backs
 * `--composer-h` (Hunt chat composer) and `--ad-bar-h` (the bottom ad bar),
 * so the ad can stack above a page-owned bar and pages can reserve room for
 * the ad without either side guessing a height.
 */
export function usePublishStickyCtaHeight(
	ref: RefObject<HTMLElement | null>,
	active = true,
	cssVar: string = STICKY_CTA_HEIGHT_VAR,
): void {
	useLayoutEffect(() => {
		const el = ref.current;
		if (!active || !el) return;
		const root = document.documentElement;
		const update = () =>
			root.style.setProperty(
				cssVar,
				`${Math.ceil(el.getBoundingClientRect().height)}px`,
			);
		update();
		const ro =
			typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
		ro?.observe(el);
		return () => {
			ro?.disconnect();
			root.style.setProperty(cssVar, "0px");
		};
	}, [ref, active, cssVar]);
}
